<template>
  <DatePicker
    v-model="date"
    :variant="variant"
    :position="position"
    :auto-position="autoPosition"
    :disabled-dates="disabledDates"
    :range="getRangeConfig()"
    :allow-open-end="allowOpenEnd"
  >
    <template #hint>
      <template v-if="allowOpenEnd">
        Select a start date (end date optional) - max range {{ MAX_RANGE }} days
      </template>
      <template v-else> You can select a range of up to {{ MAX_RANGE }} days </template>
    </template>
  </DatePicker>
</template>

<script setup lang="ts">
  // Types
  import type { PropType } from 'vue';
  import type {
    TDateRange,
    TDateRangePayload,
    IDatePickerRangeConfig,
  } from '~/misc/date-picker-range/types';

  // Config
  import { MAX_RANGE } from '~/misc/date-picker-range/config';

  // Utils
  import { DateTimeFormatter } from '~/misc/utils/datetime';

  // Components
  import DatePicker from '~/misc/date-picker/DatePicker.vue';

  const props = defineProps({
    select: {
      type: Object as PropType<TDateRangePayload>,
      default: () => ({}),
    },
    variant: {
      type: String as PropType<'default' | 'compact'>,
      default: 'default',
    },
    position: {
      type: String as PropType<'left' | 'center' | 'right'>,
      default: 'center',
    },
    autoPosition: {
      type: [Boolean, String] as PropType<boolean | 'top' | 'bottom'>,
      default: false,
    },
    allowOpenEnd: {
      type: Boolean,
      default: false,
    },
    showHint: {
      type: Boolean,
      default: false,
    },
    maxRangeLimit: {
      type: Boolean,
      default: false,
    },
  });

  const date: Ref<[string, string] | string | null> = ref<[string, string] | string | null>(null);

  const setDate = (value: [string, string] | string | null) => {
    date.value = value;
  };

  const getRangeConfig = (): IDatePickerRangeConfig => {
    if (props.allowOpenEnd) {
      return {
        partialRange: true,
        maxRange: props.maxRangeLimit ? MAX_RANGE : undefined,
        noDisabledRange: true,
      };
    }

    return {
      partialRange: true, // Allow partial selection temporarily to handle single date selection
      maxRange: props.maxRangeLimit ? MAX_RANGE : undefined,
    };
  };

  const disabledDates = (dateToCheck: Date) => {
    const today: Date = new Date();
    today.setHours(0, 0, 0, 0);

    return dateToCheck < today;
  };

  const emit = defineEmits<{
    (e: 'update', data: TDateRange): void;
  }>();

  const createDateRangeFromValue = (value: string | [string, string] | null): TDateRange => {
    if (!value) {
      return { startDate: '', endDate: '' };
    }

    if (typeof value === 'string') {
      return { startDate: value, endDate: undefined };
    }

    const [startDate, endDate]: [string, string | undefined] = value;

    return { startDate, endDate: endDate || undefined };
  };

  watch(date, (newDate: [string, string] | string | null) => {
    emit('update', createDateRangeFromValue(newDate));
  });

  watch(
    () => props.select,
    (newSelect: TDateRangePayload) => {
      if (newSelect) {
        if (newSelect.start_date && newSelect.end_date) {
          const startDate: string = DateTimeFormatter.toIsoDate(newSelect.start_date);
          const endDate: string = DateTimeFormatter.toIsoDate(newSelect.end_date);
          setDate([startDate, endDate]);
        } else if (newSelect.start_date && !newSelect.end_date && props.allowOpenEnd) {
          const startDate: string = DateTimeFormatter.toIsoDate(newSelect.start_date);
          setDate(startDate);
        } else {
          setDate(null);
        }
      }
    },
    { deep: true, immediate: true }
  );
</script>
